import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import { styled } from '@mui/material/styles';
import { useProjectsForm } from '@buildwise/libs/projects';
import RenderFilterFieldByType from '@buildwise/libs/feed/components/RenderFilterFieldByType';
import { Controller, FormProvider } from 'react-hook-form';
import { ProjectType } from '@buildwise/pages/projects/hooks/useProjectsQuery';
import { FieldLabel } from 'components/modals/addPostModal/PostDetailsForm';
import { NumericInputField } from '@buildwise/libs/forms/components/NumericInputField';
import { theme } from 'config/theme';
import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import { USStates } from 'constants/USStates';
import { SyntheticEvent } from 'react';
import AlertModal from 'components/utils/AlertModal';
import Editor, { initConfig } from 'components/common/editor/Editor';

type AddProjectModalProps = {
  project?: ProjectType;
  openAddProjectModal: boolean;
  isEditing?: boolean;
  createProjectFromAddPostModal?: { articleFormState: PostFormData; projectName: string };
  setOpenAddProjectModal: React.Dispatch<React.SetStateAction<boolean>>;
};

function AddProjectModal({
  project,
  openAddProjectModal,
  isEditing = false,
  createProjectFromAddPostModal,
  setOpenAddProjectModal,
}: AddProjectModalProps) {
  const {
    projectForm,
    control,
    isSubmitting,
    isSaveDisabled,
    singularProjectsModuleName,
    openConfirmation,
    customInputFields,
    onSubmit,
    closeModal,
    setValue,
    setOpenConfirmation,
    handleClose,
  } = useProjectsForm({
    project,
    setOpen: setOpenAddProjectModal,
    createProjectFromAddPostModal,
  });

  return (
    <Dialog
      open={openAddProjectModal}
      maxWidth={'md'}
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          height: '90%',
          transition: 'all 0.2s',
        },
      }}
    >
      <CustomDialogTitle onClose={handleClose}>
        <Title>
          {isEditing
            ? `Edit ${singularProjectsModuleName} Details`
            : `Create New ${singularProjectsModuleName}`}
        </Title>
      </CustomDialogTitle>
      <DialogContent
        dividers
        sx={{
          paddingTop: '24px',
        }}
      >
        <FormProvider {...projectForm}>
          <Grid container spacing={2} sx={{ '& > .MuiGrid-item': { paddingTop: 0 } }}>
            {customInputFields.map((field, index) => (
              <Grid
                item
                key={field.entryCode}
                sx={{ mb: 2 }}
                xs={
                  field.entryCode === 'name'
                    ? 9
                    : ['address', 'description'].includes(field.entryCode)
                      ? 12
                      : field.entryCode === 'squareFeet'
                        ? 3
                        : 4
                }
              >
                {field?.entryCode === 'description' ? (
                  <>
                    <Box mt={1} mb={1}>
                      <FieldLabel
                        id={field.entryCode}
                        disabled={isSubmitting}
                        sx={{
                          fontWeight: 400,
                          fontSize: 14,
                          color: theme.palette.custom.mistBlue,
                        }}
                        required={field.referenceObject?.isRequired}
                        htmlFor={field.entryCode}
                      >
                        {field.referenceObject?.label || field.entryDescription}
                      </FieldLabel>
                    </Box>
                    <Controller
                      control={control}
                      render={({ field: { ref, onChange, ...rest } }) => (
                        <Editor
                          {...field}
                          id={field.entryCode}
                          onEditorChange={onChange}
                          value={!!rest.value.trim() ? rest.value : ''}
                          disabled={isSubmitting}
                          init={{
                            // max_height: 500,
                            min_height: 250,
                            height: 250,
                            placeholder: 'Description',
                            resize: false,
                            plugins: [...(initConfig()?.plugins || []), 'autoresize'],
                          }}
                          onBlur={() => {
                            rest.onBlur();
                            setValue('description', rest.value.trim(), {
                              shouldDirty: true,
                              shouldValidate: true,
                            });
                          }}
                        />
                      )}
                      name="description"
                    />
                  </>
                ) : field?.entryCode === 'squareFeet' ? (
                  <Controller
                    control={control}
                    render={({ field: _field, fieldState }) => (
                      <FormControl fullWidth>
                        <TextField
                          {..._field}
                          id="formatted-numberformat-input"
                          InputProps={{
                            inputComponent: NumericInputField as any,
                            inputProps: { suffix: ' sq ft' },
                          }}
                          label={field.referenceObject?.label || 'Select'}
                          required={field.referenceObject?.isRequired}
                          value={_field.value.trim() ? _field.value : ''}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message || ''}
                          fullWidth
                          variant="standard"
                          disabled={isSubmitting}
                        />
                      </FormControl>
                    )}
                    name="squareFeet"
                  />
                ) : field?.entryCode === 'state_code' ? (
                  <Controller
                    control={control}
                    render={({ field: _field, fieldState }) => (
                      <Box flex={1}>
                        <FormControl fullWidth>
                          <Autocomplete
                            {..._field}
                            autoHighlight
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            value={
                              _field.value
                                ? USStates.find((state) => state.value === _field.value) || {
                                    name: _field.value,
                                    value: _field.value,
                                  }
                                : null
                            }
                            onChange={(
                              _: SyntheticEvent<Element>,
                              value: {
                                name: string;
                                value: string;
                              } | null,
                            ) =>
                              setValue('state_code', value?.value || '', {
                                shouldDirty: true,
                                shouldValidate: true,
                              })
                            }
                            options={USStates}
                            getOptionLabel={(option) => option.name}
                            noOptionsText="No States Found"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={!!fieldState.error}
                                helperText={fieldState?.error?.message || ''}
                                variant="standard"
                                label={field.referenceObject?.label || 'Select'}
                                required={field.referenceObject?.isRequired}
                              />
                            )}
                            disabled={isSubmitting}
                          />
                        </FormControl>
                      </Box>
                    )}
                    name={field.entryCode}
                  />
                ) : (
                  <RenderFilterFieldByType field={field} autoFocus={index === 0} showLabel />
                )}
              </Grid>
            ))}
          </Grid>
        </FormProvider>
        {openConfirmation && (
          <AlertModal
            open={openConfirmation}
            setOpen={setOpenConfirmation}
            handleCancel={closeModal}
            onSubmit={onSubmit}
            loading={isSubmitting}
            title="Confirm"
            submitButtonName={'Save Changes'}
            cancelButtonName="Discard"
            description={'Are you sure you want to discard your changes?'}
            discardActions
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" sx={{ mr: 1 }} onClick={closeModal}>
          Cancel
        </Button>
        <Button disabled={isSaveDisabled} onClick={onSubmit} variant="contained">
          {isSubmitting ? (
            <CircularProgress sx={(theme) => ({ color: theme.palette.custom.black })} size={20} />
          ) : isEditing ? (
            'Save'
          ) : (
            'Add'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const Title = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontSize: 24,
  fontWeight: 700,
  color: theme.palette.custom.black,
}));

export default AddProjectModal;
